import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AuthApiService, MessageResponse } from "../../../core/services/auth.api.service";
import { UserState } from "../../../core/states/user-state";
import { UserModel } from "../../models/user.model";
import { LoginWithPasswordPayload } from "../../models/auth/login-with-password-payload";
import { ReLoginWithTokenPayload } from "../../models/auth/re-login-with-token-payload";
import { BrowserStorageService, StorageKeys } from "../../../core/services/browser-storage.service";
import { SignUpWithPasswordPayload } from '../../models/auth/signup-payload';

export class AuthenticationManagerOperation {
    constructor(
        private authService: AuthApiService,
        private userState: UserState,
        private router: Router,
        private browserStorageService: BrowserStorageService
    ) { }

    login(username: string, password: string, rememberMe = false): Observable<UserModel | { requiresOtp: true; email: string }> {
        const payload = new LoginWithPasswordPayload(username, rememberMe, password);
        return this.authenticateWithPassword(payload);
    }

    relogin(username: string, token: string, rememberMe = false): Observable<UserModel> {
        const payload = new ReLoginWithTokenPayload(username, rememberMe, token);
        return this.authenticateWithToken(payload);
    }

    /** Returns UserModel on success, or { requiresOtp: true, email } when admin must enter OTP from their email. */
    private authenticateWithPassword(payload: LoginWithPasswordPayload): Observable<UserModel | { requiresOtp: true; email: string }> {
        return this.authService.authenticateWithPassword(payload).pipe(
            tap((res: UserModel | { requiresOtp: true; email: string }) => {
                if (res && 'requiresOtp' in res && res.requiresOtp) return;
                const user = res as UserModel;
                this.userState.setUser(user, payload.getPayload().rememberMe);
                this.storeToken(user.token, payload.getPayload().rememberMe);
            }),
            catchError(error => {
                console.error('Authentication error:', error);
                return throwError(() => error);
            })
        );
    }

    private authenticateWithToken(payload: ReLoginWithTokenPayload): Observable<UserModel> {
        return this.authService.authenticateWithToken(payload).pipe(
            tap((user: UserModel) => {
                this.userState.setUser(user, payload.getPayload().rememberMe);
                this.storeToken(user.token, payload.getPayload().rememberMe);
                this.router.navigate(['/dashboard']); // Redirect to dashboard
            }),
            catchError(error => {
                console.error('Authentication error:', error);
                return throwError(() => error);
            })
        );
    }

    logout(): void {
        this.userState.clear();
        // console.log(this.userState.getUser())
        this.router.navigate(['/login']);
    }

    isAuthenticated(): boolean {
        return this.userState.getAuthenticated();
    }

    setUserFromPasskey(user: UserModel, rememberMe: boolean): void {
        this.userState.setUser(user, rememberMe);
        this.storeToken(user.token, rememberMe);
    }

    /** Persist JWT in localStorage so all tabs share the session; cleared on logout. */
    private storeToken(token: string, _rememberMe: boolean): void {
        this.browserStorageService.removeSessionItem(StorageKeys.Authentication);
        this.browserStorageService.setLocalItem(StorageKeys.Authentication, token);
    }

    nonjointSignup(username: string, password: string, fullName: string, loanId: string, esignConsent: boolean, rememberMe = false): Observable<MessageResponse> {
        const payload = new SignUpWithPasswordPayload(username, rememberMe, password, fullName, esignConsent);
        return this.authService.nonjointSignup(payload, loanId).pipe(
            tap((_res: MessageResponse) => {
                // Intentionally do not set an authenticated session.
                // Users must verify email and then login.
            }),
            catchError(error => {
                console.error('Authentication error:', error);
                return throwError(() => error);
            })
        );
    }

    signup(username: string, password: string, fullName: string, esignConsent: boolean, rememberMe = false): Observable<MessageResponse> {
        const payload = new SignUpWithPasswordPayload(username, rememberMe, password, fullName, esignConsent);
        return this.authService.signup(payload).pipe(
            tap((_res: MessageResponse) => {
                // Intentionally do not set an authenticated session.
                // Users must verify email and then login.
            }),
            catchError(error => {
                console.error('Authentication error:', error);
                return throwError(() => error);
            })
        );
    }
}
